import { actionLog, createBoundedUseStore } from '@core/store/util';

import { createCrossFrameStore } from '../util/createCrossFrameStore';

import { createAiAgentSlice, type AiAgentSlice } from './Agent';
import { createAiLinterSlice, type AiLinterSlice } from './Linter';

export type AIStore = AiAgentSlice &
  AiLinterSlice & {
    /*
     * A utility function to reset the AI store to its initial state.
     * Currently, this is only used in tests.
     */
    reset: () => void;
  };

/**
 * Vanilla store that contains global state for our AI related features.
 * This store can be accessed and used anywhere. React components
 * should call `useAIStore()` instead.
 * @example
 * import { aiStore } from '@core/store';
 *
 * const checkpoints = aiStore.getState().agent.checkpoints;
 */
export const aiStore = createCrossFrameStore<AIStore>('AIStore', (set, get, ...props) => {
  const initialState = {
    ...createAiAgentSlice(set, get, ...props),
    ...createAiLinterSlice(set, get, ...props),

    reset: () => {
      set({ ...initialState }, false, actionLog('ai.reset', []));
    },
  };

  return {
    ...initialState,
  };
});

/**
 * Bound react hook to access our AI store. Must be called within a React
 * component. To access the store outside of React, use `aiStore` instead.
 * @example
 * import { useAIStore } from '@core/store';
 *
 * function Component() {
 *   const checkpoints = useAIStore(s => s.agent.checkpoints);
 * }
 */
export const useAIStore = createBoundedUseStore(aiStore);
